import React from 'react'
import { Link } from 'gatsby'
import slugify from 'slugify'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import VideoPlayer from '~/components/slices/videoPlayer'
import Table from '~/components/slices/table'
import Testimony from '~/components/slices/testimony'
import CookiesSettings from '~/components/core/cookiesSettings'
import { contentModel } from '~/utils/contentModel'

export const WysiwygApiRest = (props) => {
  const { document, isProject, hasTitle, lang, newsroomUrls } = props

  const renderEmbed = (type, video, url, alt, testimony, isProject) => {
    switch (type) {
      case contentModel.front_img:
        return (
          <img className="lazyload block mx-auto" src={url} alt={alt[lang]} />
        )
      case contentModel.video:
        return (
          <VideoPlayer data={video} isWysiwyg={true} sliceHasTitle={hasTitle} />
        )
      case contentModel.testimony:
        return (
          <>
            {isProject ? <Testimony data={testimony} isProject={true} /> : ''}
          </>
        )
      default:
        return 'error'
    }
  }

  const optionsForDataFromRestApi = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // case image or video
        const type = node.data.target.sys.contentType.sys.id
        let image, alt, url
        let video = {}
        let testimony = {}

        if (type === 'front_img') {
          image = node.data.target.fields
          url = image.file.fr
            ? image.file.fr.fields.file[lang].url
            : image.file.fields.file.url
          alt = Array.isArray(image.altImage)
            ? image.altImage[lang]
            : image.altImage
        } else if (type === 'video') {
          video.title = node.data.target.fields.title.fr
            ? node.data.target.fields.title[lang]
            : node.data.target.fields.title
          video.cover = node.data.target.fields.cover.fr
            ? node.data.target.fields.cover.fr
            : node.data.target.fields.cover
          video.youtubeId = node.data.target.fields.youtubeId.fr
            ? node.data.target.fields.youtubeId[lang]
            : node.data.target.fields.youtubeId

          if (node.data.target.fields.youtubePlaylistId) {
            video.youtubePlaylistId = node.data.target.fields.youtubePlaylistId
              .fr
              ? node.data.target.fields.youtubePlaylistId[lang]
              : node.data.target.fields.youtubePlaylistId
          }
        } else {
          testimony.author = node.data.target.fields.author.fr
            ? node.data.target.fields.author[lang]
            : node.data.target.fields.author
          testimony.theme = node.data.target.fields.theme.fr
            ? node.data.target.fields.theme[lang]
            : node.data.target.fields.theme
          testimony.content = node.data.target.fields.content.fr
            ? node.data.target.fields.content[lang]
            : node.data.target.fields.content
          testimony.job = node.data.target.fields.job.fr
            ? node.data.target.fields.job[lang]
            : node.data.target.fields.job
          testimony.image = node.data.target.fields.image.fr.fields
            ? node.data.target.fields.image[lang].fields
            : node.data.target.fields.image.fields

          testimony.image.altImage = testimony.image.altImage.fr
            ? testimony.image.altImage[lang]
            : testimony.image.altImage
          testimony.image.file = testimony.image.file.fr
            ? testimony.image.file[lang]
            : testimony.image.file
          testimony.image.file.file = testimony.image.file.fields.file.fr
            ? testimony.image.file.fields.file[lang]
            : testimony.image.file.fields.file
        }

        return <>{renderEmbed(type, video, url, alt, testimony, isProject)}</>
      },
      [BLOCKS.TABLE]: (node) => {
        return <Table datas={node} />
      },
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        const el = node.data.target.fields
        const text = el.text.fr ? el.text.fr : el.text
        const type = el.type.fr ? el.type.fr : el.type

        return <>{type === 'Indice' ? <sub>{text}</sub> : <sup>{text}</sup>}</>
      },
      [INLINES.ASSET_HYPERLINK]: (node) => {
        const text = node.content[0].value
        const file = node.data.target.fields.file
        const url = file.fr ? file[lang].url : file.url

        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        )
      },
      [INLINES.ENTRY_HYPERLINK]: (node) => {
        let contentType = node?.data?.target?.sys?.contentType?.sys?.id
        let contentTypeContentful = contentType
        const text = node.content[0].value
        const slugUrls = [
          'page',
          'contact',
          'governance',
          'hub',
          'map',
          'newsroomList',
          'profile',
          'simulator',
          'datas-odre',
        ]
        let url = null

        if (slugUrls.indexOf(contentType) !== -1) {
          if (contentType !== 'page') {
            contentType = 'slugPages'
          } else {
            let customType = node.data.target.fields.customType['fr']

            if (customType === 'Médiathèque') {
              contentType = 'medias'
            } else if (customType === 'Blog') {
              contentType = 'blog'
            } else {
              contentType = 'slugPages'
            }
          }
        }

        const defaultTitle =
          node?.data?.target?.fields?.title?.[lang] ??
          node?.data?.target?.fields?.title

        const slugifiedDefaultTitle = slugify(defaultTitle ?? '', {
          remove: /[*+~.()'"!?:@,]/g,
        }).toLowerCase()

        switch (contentType) {
          case contentModel.news:
            url = `/${newsroomUrls['Actualités'][lang]}/${slugifiedDefaultTitle}`
            break
          case contentModel.action:
            url = `/${newsroomUrls['Actions'][lang]}/${slugifiedDefaultTitle}`
            break
          case contentModel.blog:
            url = `/${newsroomUrls['Blog'][lang]}/${slugifiedDefaultTitle}`
            break
          case contentModel.medias:
            url = `/${newsroomUrls['Médiathèque'][lang]}/${slugifiedDefaultTitle}`
            break
          case contentModel.project:
            url = `${lang === 'fr' ? '/projet' : '/en/project'}/${slugifiedDefaultTitle}`
            break
          case contentModel.event:
            url = `/${newsroomUrls['Événements'][lang]}/${slugifiedDefaultTitle}`
            break
          case contentModel.labo:
            url = `${lang === 'fr' ? '/labo' : '/en/lab'}/${slugifiedDefaultTitle}`
            break
          case contentModel.slugPages:
            let slug =
              typeof node?.data?.target?.fields?.slug === 'object'
                ? node?.data?.target?.fields?.slug?.[lang]
                : node?.data?.target?.fields?.slug
            url = `${lang === 'fr' ? '/' : '/en/'}${slug}`
            break
          case contentModel.homepage:
            url = `${lang === 'fr' ? '/' : '/en'}`
            break
          default:
            url = null
        }

        return (
          <>
            {contentTypeContentful === 'simulator' ||
            contentTypeContentful === 'contact' ? (
              <a href={url}>{text}</a>
            ) : (
              <Link to={url}>{text}</Link>
            )}
          </>
        )
      },
      [INLINES.HYPERLINK]: (node) => {
        const url = node.data.uri
        const text = node.content[0].value
        const re = new RegExp('^(http|https)://', 'i')
        const mail = new RegExp('^mailto:', 'i')
        if (!url) {
          return <span>{text}</span>
        } else if (url.match(re)) {
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {text}
            </a>
          )
        } else if (url.match(mail)) {
          return <a href={url}>{text}</a>
        } else if (url === 'cookies') {
          return (
            <CookiesSettings text={text} className="show-cookies-settings" />
          )
        } else {
          return <Link to={url}>{text}</Link>
        }
      },
      [BLOCKS.HEADING_3]: (node) => {
        let title = <h3>{node.content[0].value}</h3>
        if (!hasTitle) {
          title = <h2>{node.content[0].value}</h2>
        }

        return title
      },
    },
    renderText: (text) =>
      text.split('\n').flatMap((text, i) => [i > 0 && <br key={i} />, text]),
  }

  // console.log(document)

  return documentToReactComponents(
    typeof document === 'string' ? JSON.parse(document) : document,
    optionsForDataFromRestApi,
  )
}
